import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import AccountTypeahead, { AccountTypeaheadOption } from '../account/AccountTypeahead';
import EntityDrawer, { SecondaryPanel } from '../ui/EntityDrawer';
import Tabs from '../ui/Tabs';
import { adminAPI } from '../../services/api';
import CreateInvoiceModal from '../CreateInvoiceModal';
import RecordInvoicesPanel from '../invoices/RecordInvoicesPanel';
import InvoiceDrawerHost from '../invoices/InvoiceDrawerHost';
import { CustomerTypeaheadOption } from './CustomerTypeahead';
import { Invoice } from '../../types/invoice';
import SavedPaymentMethodsPanel from '../payments/SavedPaymentMethodsPanel';

type CustomerDrawerTab = 'overview' | 'invoices' | 'payments';

interface CustomerEditData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  dateOfBirth: string;
  licenseNumber: string;
  licenseExpiry: string;
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
  companyName: string;
  companyContact: string;
  companyContactEmail: string;
  companyContactPhone: string;
  customerType: string;
  wedding: boolean;
  customerWebsite: string;
  customerStatus: string;
  syncToStripe: boolean;
}

interface CustomerDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  customer: any | null;
  headerActions?: React.ReactNode;
  allowEdit?: boolean;
  customerDetails?: any | null;
  isLoading?: boolean;
  fallbackInvoices?: any[];
  subtitlePrefix?: string;
  sourceNote?: string;
  widthClassName?: string;
}

function formatCentsToDollars(value?: number | string | null) {
  const cents = Number(value || 0);
  return (Number.isFinite(cents) ? cents : 0) / 100;
}

function asNonEmptyString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function looksLikeStripeCustomerId(value: string): boolean {
  return /^cus_[A-Za-z0-9]+$/.test(value);
}

function toSafeDate(value: unknown): Date | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    // Stripe-style timestamps can be epoch seconds.
    const date = new Date(value < 1_000_000_000_000 ? value * 1000 : value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
}

function toDateInput(value: unknown): string {
  const safeDate = toSafeDate(value);
  if (!safeDate) {
    return '';
  }

  const year = safeDate.getFullYear();
  const month = String(safeDate.getMonth() + 1).padStart(2, '0');
  const day = String(safeDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildEditDataFromCustomer(primary: any | null, fallback: any | null): CustomerEditData {
  const source = primary || {};
  const alt = fallback || {};

  return {
    email: source.email || alt.email || '',
    firstName: source.first_name || source.firstName || alt.first_name || alt.firstName || '',
    lastName: source.last_name || source.lastName || alt.last_name || alt.lastName || '',
    phone: source.phone || alt.phone || '',
    address: source.address || alt.address || '',
    city: source.city || alt.city || '',
    state: source.state || alt.state || '',
    zipCode: source.zip_code || source.zipCode || alt.zip_code || alt.zipCode || '',
    dateOfBirth: toDateInput(source.date_of_birth || source.dateOfBirth || alt.date_of_birth || alt.dateOfBirth),
    licenseNumber: source.license_number || source.licenseNumber || alt.license_number || alt.licenseNumber || '',
    licenseExpiry: toDateInput(source.license_expiry || source.licenseExpiry || alt.license_expiry || alt.licenseExpiry),
    emergencyContact: source.emergency_contact || source.emergencyContact || alt.emergency_contact || alt.emergencyContact || {
      name: '',
      phone: '',
      relationship: '',
    },
    companyName: source.customer_company_name || source.companyName || alt.customer_company_name || alt.companyName || '',
    companyContact: source.customer_company_contact || source.companyContact || alt.customer_company_contact || alt.companyContact || '',
    companyContactEmail: source.customer_company_email || source.companyContactEmail || alt.customer_company_email || alt.companyContactEmail || '',
    companyContactPhone: source.customer_company_phone || source.companyContactPhone || alt.customer_company_phone || alt.companyContactPhone || '',
    customerType: source.customer_type || source.customerType || alt.customer_type || alt.customerType || '',
    wedding: Boolean(source.wedding ?? alt.wedding ?? false),
    customerWebsite: source.customer_website || source.customerWebsite || alt.customer_website || alt.customerWebsite || '',
    customerStatus: source.status || source.customer_status || source.customerStatus || alt.status || alt.customer_status || alt.customerStatus || 'Active',
    syncToStripe: true,
  };
}

export default function CustomerDetailDrawer({
  open,
  onClose,
  customer,
  headerActions,
  allowEdit = false,
  customerDetails,
  isLoading = false,
  fallbackInvoices = [],
  subtitlePrefix = 'Stripe ID',
  sourceNote,
  widthClassName = 'max-w-3xl',
}: CustomerDetailDrawerProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [drawerTab, setDrawerTab] = useState<CustomerDrawerTab>('overview');
  const [editCustomerData, setEditCustomerData] = useState<CustomerEditData>(buildEditDataFromCustomer(null, null));
  const [selectedAccount, setSelectedAccount] = useState<AccountTypeaheadOption | null>(null);
  const [secondaryType, setSecondaryType] = useState<'account' | 'invoice' | 'payment' | 'edit' | null>(null);
  const [secondaryData, setSecondaryData] = useState<any>(null);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [selectedFullInvoice, setSelectedFullInvoice] = useState<Partial<Invoice> | null>(null);

  const rawCustomerId = asNonEmptyString(customer?.id || customer?.customerId || customer?.stripeCustomerId);
  const customerEmail = asNonEmptyString(
    customer?.email || customer?.customerEmail || customer?.company_contact_email || customer?.companyContactEmail,
  ).toLowerCase();
  const directStripeCustomerId = looksLikeStripeCustomerId(rawCustomerId) ? rawCustomerId : '';

  const {
    data: resolvedStripeCustomerId,
    isLoading: resolvingCustomerIdentity,
  } = useQuery<string | null>(
    ['customer-drawer-resolve-id', directStripeCustomerId, customerEmail],
    async () => {
      if (directStripeCustomerId) {
        return directStripeCustomerId;
      }

      if (!customerEmail) {
        return null;
      }

      const response = await adminAPI.getCustomers({
        search: customerEmail,
        page: 1,
        limit: 100,
      });
      const customers = Array.isArray(response?.data?.customers) ? response.data.customers : [];

      const exactMatch = customers.find((entry: any) => {
        const entryEmail = asNonEmptyString(entry?.email).toLowerCase();
        const entryId = asNonEmptyString(entry?.id);
        return entryEmail === customerEmail && looksLikeStripeCustomerId(entryId);
      });

      return exactMatch ? asNonEmptyString(exactMatch.id) : null;
    },
    {
      enabled: open && Boolean(customer) && Boolean(directStripeCustomerId || customerEmail),
      staleTime: 60_000,
      retry: false,
    },
  );

  const effectiveStripeCustomerId = directStripeCustomerId || resolvedStripeCustomerId || null;

  const {
    data: fetchedCustomerDetails,
    isLoading: loadingCustomerDetails,
  } = useQuery<any | null>(
    ['customer-drawer-profile', effectiveStripeCustomerId],
    async () => {
      if (!effectiveStripeCustomerId) {
        return null;
      }

      const response = await adminAPI.getCustomer(effectiveStripeCustomerId);
      return response.data;
    },
    {
      enabled: open && Boolean(effectiveStripeCustomerId),
      staleTime: 60_000,
      retry: false,
    },
  );

  const {
    data: fetchedInvoices,
    isLoading: loadingCustomerInvoices,
  } = useQuery<any[]>(
    ['customer-drawer-invoices', customerEmail],
    async () => {
      if (!customerEmail) {
        return [];
      }

      const response = await adminAPI.getAllInvoices({
        page: 1,
        limit: 100,
        customerScope: `email:${customerEmail}`,
      });

      return Array.isArray(response?.data?.invoices) ? response.data.invoices : [];
    },
    {
      enabled: open && Boolean(customerEmail),
      staleTime: 60_000,
      retry: false,
    },
  );

  useEffect(() => {
    if (open) {
      setDrawerTab('overview');
      setSecondaryType(null);
      setSecondaryData(null);
    }
  }, [open, customer?.id]);

  const resolvedCustomerDetails = fetchedCustomerDetails || customerDetails || null;
  const customerStats = resolvedCustomerDetails?.statistics || null;
  const profileCustomer = resolvedCustomerDetails?.customer || null;
  const historicalProviderIds: string[] = Array.from(new Set([...(profileCustomer?.historical_stripe_customer_ids || []), ...(customer?.historical_stripe_customer_ids || [])]));
  const canonicalActivityReady = historicalProviderIds.length === 0 || Boolean(
    profileCustomer?.local_customer_id && profileCustomer?.canonical_stripe_customer_id &&
    !historicalProviderIds.includes(profileCustomer.canonical_stripe_customer_id)
  );
  const profileAccountId = profileCustomer?.account?.id || null;
  const profileAccountName = profileCustomer?.account?.name || null;

  useEffect(() => {
    if (!open) {
      return;
    }

    setEditCustomerData(buildEditDataFromCustomer(profileCustomer, customer));
  }, [open, profileCustomer, customer]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedAccount(null);
  }, [open, customer?.id]);

  const displayEmail = profileCustomer?.email || customer?.email || customer?.customerEmail || 'N/A';
  const displayPhone = profileCustomer?.phone || customer?.phone || customer?.company_contact_phone || customer?.companyContactPhone || 'N/A';
  const displayExternalCustomerId = profileCustomer?.external_customer_id || customer?.external_customer_id || customer?.externalCustomerId || '';
  const displayStatus = profileCustomer?.customer_status || profileCustomer?.status || customer?.customer_status || customer?.status || 'Unknown';
  const displayType = customer?.customer_type || 'Stripe';
  const displayCreated = customer?.registrationDate || profileCustomer?.created_at || customerStats?.customerSince;
  const displayLastActivity = customer?.lastActivity || customerStats?.lastBookingDate;

  const drawerLoading = isLoading || resolvingCustomerIdentity || loadingCustomerDetails || loadingCustomerInvoices;

  const customerRecentInvoices = useMemo(() => {
    if (profileCustomer?.historical_stripe_customer_ids?.length || customer?.historical_stripe_customer_ids?.length) {
      const history = resolvedCustomerDetails?.recentActivity?.invoices;
      return Array.isArray(history) ? history : [];
    }
    if (Array.isArray(fetchedInvoices) && fetchedInvoices.length > 0) {
      return fetchedInvoices;
    }

    const fromDetails = resolvedCustomerDetails?.recentActivity?.invoices;
    if (Array.isArray(fromDetails) && fromDetails.length > 0) {
      return fromDetails;
    }
    return Array.isArray(fallbackInvoices) ? fallbackInvoices : [];
  }, [resolvedCustomerDetails, fallbackInvoices, fetchedInvoices, profileCustomer, customer]);

  const attachedAccounts = useMemo(() => {
    if (!profileAccountId) {
      return [];
    }

    return [{
      id: profileAccountId,
      name: profileAccountName || 'Attached account',
    }];
  }, [profileAccountId, profileAccountName]);

  const paidInvoices = customerRecentInvoices.filter((invoice: any) => {
    const status = typeof invoice?.status === 'string' ? invoice.status.toLowerCase() : '';
    return status === 'paid' || Number(invoice?.amount_paid || 0) > 0;
  });

  const initialInvoiceCustomer = useMemo<CustomerTypeaheadOption | null>(() => {
    const id =
      profileCustomer?.local_customer_id ||
      customer?.local_customer_id ||
      customer?.customer_record_id ||
      effectiveStripeCustomerId ||
      profileCustomer?.id ||
      rawCustomerId;
    if (!id) return null;
    const firstName = profileCustomer?.first_name || customer?.firstName || customer?.first_name || '';
    const lastName = profileCustomer?.last_name || customer?.lastName || customer?.last_name || '';
    const label = [firstName, lastName].filter(Boolean).join(' ').trim()
      || profileCustomer?.name
      || customer?.customerName
      || customer?.name
      || displayEmail
      || 'Customer';
    return {
      id,
      label,
      description: displayEmail !== 'N/A' ? displayEmail : undefined,
      email: displayEmail !== 'N/A' ? displayEmail : '',
    };
  }, [customer, displayEmail, effectiveStripeCustomerId, profileCustomer, rawCustomerId]);

  const initialInvoiceAccount = useMemo<AccountTypeaheadOption | null>(() => {
    if (!profileAccountId) return null;
    return {
      id: profileAccountId,
      label: profileAccountName || 'Attached account',
      description: 'Linked customer account',
    };
  }, [profileAccountId, profileAccountName]);

  const crmActivityItems = useMemo(() => {
    const activities: Array<{
      id: string;
      kind: 'invoice_sent' | 'payment_received' | 'grouped_request_sent' | 'grouped_request_paid';
      happenedAt: Date;
      invoiceLabel: string;
      amountCents: number;
      invoiceCount?: number;
    }> = [];

    const groupedSentActivity = new Map<string, {
      requestId: string;
      happenedAt: Date;
      amountCents: number;
      invoiceIds: Set<string>;
      reportedInvoiceCount: number;
    }>();

    const groupedPaidActivity = new Map<string, {
      requestId: string;
      happenedAt: Date;
      amountCents: number;
      invoiceIds: Set<string>;
      reportedInvoiceCount: number;
    }>();

    customerRecentInvoices.forEach((invoice: any, index: number) => {
      const invoiceId = String(invoice?.id || `idx-${index}`);
      const invoiceLabel = invoice?.invoice_number || invoice?.number || `Invoice ${index + 1}`;
      const invoiceTotal = Number(invoice?.total || 0);
      const amountPaid = Number(invoice?.amount_paid || 0);
      const groupedRequestId = String(invoice?.grouped_payment_request_id || invoice?.grouped_request_id || '').trim();
      const groupedInvoiceCount = Number(invoice?.grouped_payment_request_invoice_count || 0);

      const sentAt = toSafeDate(invoice?.sent_at || invoice?.issued_at || invoice?.created_at);
      if (sentAt) {
        if (groupedRequestId) {
          const existingGroup = groupedSentActivity.get(groupedRequestId);
          if (existingGroup) {
            if (sentAt.getTime() > existingGroup.happenedAt.getTime()) {
              existingGroup.happenedAt = sentAt;
            }
            existingGroup.amountCents += invoiceTotal;
            existingGroup.invoiceIds.add(invoiceId);
            existingGroup.reportedInvoiceCount = Math.max(existingGroup.reportedInvoiceCount, groupedInvoiceCount);
          } else {
            groupedSentActivity.set(groupedRequestId, {
              requestId: groupedRequestId,
              happenedAt: sentAt,
              amountCents: invoiceTotal,
              invoiceIds: new Set([invoiceId]),
              reportedInvoiceCount: groupedInvoiceCount,
            });
          }
        } else {
          activities.push({
            id: `sent-${invoice?.id || index}-${sentAt.getTime()}`,
            kind: 'invoice_sent',
            happenedAt: sentAt,
            invoiceLabel,
            amountCents: invoiceTotal,
          });
        }
      }

      const paidAt = toSafeDate(invoice?.paid_at || invoice?.status_transitions?.paid_at);
      if (paidAt || amountPaid > 0) {
        const fallbackPaidAt = paidAt || toSafeDate(invoice?.updated_at) || toSafeDate(invoice?.created_at);
        if (fallbackPaidAt) {
          if (groupedRequestId) {
            const existingGroup = groupedPaidActivity.get(groupedRequestId);
            if (existingGroup) {
              if (fallbackPaidAt.getTime() > existingGroup.happenedAt.getTime()) {
                existingGroup.happenedAt = fallbackPaidAt;
              }
              existingGroup.amountCents += amountPaid || invoiceTotal;
              existingGroup.invoiceIds.add(invoiceId);
              existingGroup.reportedInvoiceCount = Math.max(existingGroup.reportedInvoiceCount, groupedInvoiceCount);
            } else {
              groupedPaidActivity.set(groupedRequestId, {
                requestId: groupedRequestId,
                happenedAt: fallbackPaidAt,
                amountCents: amountPaid || invoiceTotal,
                invoiceIds: new Set([invoiceId]),
                reportedInvoiceCount: groupedInvoiceCount,
              });
            }
          } else {
            activities.push({
              id: `paid-${invoice?.id || index}-${fallbackPaidAt.getTime()}`,
              kind: 'payment_received',
              happenedAt: fallbackPaidAt,
              invoiceLabel,
              amountCents: amountPaid || invoiceTotal,
            });
          }
        }
      }
    });

    groupedSentActivity.forEach((group) => {
      activities.push({
        id: `group-sent-${group.requestId}-${group.happenedAt.getTime()}`,
        kind: 'grouped_request_sent',
        happenedAt: group.happenedAt,
        invoiceLabel: `Request ${group.requestId.slice(0, 8)}`,
        amountCents: group.amountCents,
        invoiceCount: Math.max(group.invoiceIds.size, group.reportedInvoiceCount),
      });
    });

    groupedPaidActivity.forEach((group) => {
      activities.push({
        id: `group-paid-${group.requestId}-${group.happenedAt.getTime()}`,
        kind: 'grouped_request_paid',
        happenedAt: group.happenedAt,
        invoiceLabel: `Request ${group.requestId.slice(0, 8)}`,
        amountCents: group.amountCents,
        invoiceCount: Math.max(group.invoiceIds.size, group.reportedInvoiceCount),
      });
    });

    return activities
      .sort((a, b) => b.happenedAt.getTime() - a.happenedAt.getTime())
      .slice(0, 10);
  }, [customerRecentInvoices]);

  const assignAccountMutation = useMutation(
    async (accountOption: AccountTypeaheadOption | null) => {
      if (!customerEmail) {
        throw new Error('Customer email is required');
      }

      return adminAPI.assignCustomerAccountByIdentity({
        accountId: accountOption?.id || null,
        email: customerEmail,
        firstName: profileCustomer?.first_name || customer?.first_name || customer?.firstName || null,
        lastName: profileCustomer?.last_name || customer?.last_name || customer?.lastName || null,
        phone: profileCustomer?.phone || customer?.phone || null,
        stripeCustomerId: effectiveStripeCustomerId,
      });
    },
    {
      onSuccess: async (_response, accountOption) => {
        toast.success(accountOption ? 'Account attached to customer' : 'Account removed from customer');
        setSelectedAccount(null);
        await Promise.all([
          queryClient.invalidateQueries(['customer-drawer-profile', effectiveStripeCustomerId]),
          queryClient.invalidateQueries('admin-accounts'),
        ]);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to update account');
      },
    },
  );

  const updateCustomerMutation = useMutation(
    async (payload: CustomerEditData) => {
      const targetCustomerId = profileCustomer?.local_customer_id || customer?.local_customer_id || customer?.id || effectiveStripeCustomerId;
      if (!targetCustomerId) {
        throw new Error('Customer id is unavailable');
      }

      return adminAPI.updateCustomer(targetCustomerId, payload);
    },
    {
      onSuccess: async () => {
        toast.success('Customer updated successfully');
        await Promise.all([
          queryClient.invalidateQueries('admin-customers'),
          queryClient.invalidateQueries(['customer-drawer-profile', effectiveStripeCustomerId]),
          queryClient.invalidateQueries(['customer-drawer-invoices', customerEmail]),
        ]);
        setSecondaryType(null);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to update customer');
      },
    },
  );

  const profileName = [profileCustomer?.first_name, profileCustomer?.last_name].filter(Boolean).join(' ');

  const title = customer
    ? (profileName || [customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.customerName || customer.email)
    : 'Customer';

  const subtitleId = effectiveStripeCustomerId || customer?.id || customer?.customerId;

  const drawerHeaderActions = (
    <>
      {allowEdit && customer && canonicalActivityReady ? (
        <button
          type="button"
          onClick={() => {
            setEditCustomerData(buildEditDataFromCustomer(profileCustomer, customer));
            setSecondaryType('edit');
          }}
          className="rounded-lg p-1 text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface"
          aria-label="Edit customer"
          title="Edit customer"
        >
          <PencilSquareIcon className="h-5 w-5" />
        </button>
      ) : null}
      {headerActions}
    </>
  );

  const secondary: SecondaryPanel | undefined = (() => {
    if (secondaryType === 'edit' && customer) {
      return {
        open: true,
        onClose: () => setSecondaryType(null),
        title: 'Edit Customer',
        subtitle: 'Update customer profile',
        children: (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              updateCustomerMutation.mutate(editCustomerData);
            }}
          >
            <div className="rounded-card border border-outline/60 p-3">
              <label className="flex items-center gap-2 text-sm text-on-surface">
                <input
                  type="checkbox"
                  checked={editCustomerData.syncToStripe}
                  onChange={(e) => setEditCustomerData({ ...editCustomerData, syncToStripe: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>Sync changes to Stripe customer</span>
              </label>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">First Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={editCustomerData.firstName}
                  onChange={(e) => setEditCustomerData({ ...editCustomerData, firstName: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">Last Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={editCustomerData.lastName}
                  onChange={(e) => setEditCustomerData({ ...editCustomerData, lastName: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={editCustomerData.email}
                  onChange={(e) => setEditCustomerData({ ...editCustomerData, email: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">Phone</label>
                <input
                  type="tel"
                  className="input-field"
                  value={editCustomerData.phone}
                  onChange={(e) => setEditCustomerData({ ...editCustomerData, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">Company Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={editCustomerData.companyName}
                  onChange={(e) => setEditCustomerData({ ...editCustomerData, companyName: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">Status</label>
                <select
                  className="input-field"
                  value={editCustomerData.customerStatus}
                  onChange={(e) => setEditCustomerData({ ...editCustomerData, customerStatus: e.target.value })}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-outline/60 pt-3">
              <button type="button" className="btn-secondary" onClick={() => setSecondaryType(null)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={updateCustomerMutation.isLoading}>
                {updateCustomerMutation.isLoading ? 'Saving...' : 'Save Customer'}
              </button>
            </div>
          </form>
        ),
      };
    }

    if (secondaryType === 'account' && profileAccountId) {
      return {
        open: true,
        onClose: () => setSecondaryType(null),
        title: profileAccountName ?? 'Account',
        subtitle: `Account · ${profileAccountId.slice(0, 8)}…`,
        children: (
          <div className="space-y-4">
            <div className="card p-4">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Name</dt>
                  <dd className="font-medium text-on-surface">{profileAccountName ?? '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Account ID</dt>
                  <dd className="font-mono text-xs text-on-surface-variant">{profileAccountId}</dd>
                </div>
              </dl>
            </div>
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => { onClose(); navigate(`/admin/accounts/${profileAccountId}`); }}
            >
              Open full account page →
            </button>
          </div>
        ),
      };
    }
    if (secondaryType === 'invoice' && secondaryData) {
      const inv = secondaryData;
      return {
        open: true,
        onClose: () => { setSecondaryType(null); setSecondaryData(null); },
        title: inv.invoice_number || 'Invoice',
        subtitle: inv.status ? `${String(inv.status).charAt(0).toUpperCase()}${String(inv.status).slice(1)}` : 'Invoice details',
        children: (
          <div className="space-y-4">
            <div className="card p-4">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Status</dt>
                  <dd className="font-medium text-on-surface">{inv.status || '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Total</dt>
                  <dd className="font-medium text-on-surface">${formatCentsToDollars(inv.total).toFixed(2)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Paid</dt>
                  <dd className="font-medium text-on-surface">${formatCentsToDollars(inv.amount_paid).toFixed(2)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Issued</dt>
                  <dd className="text-on-surface">{inv.issued_at ? new Date(inv.issued_at).toLocaleDateString() : '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Due</dt>
                  <dd className="text-on-surface">{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}</dd>
                </div>
              </dl>
            </div>
            {(inv.stripe_invoice_id || inv.payment_intent_id) && (
              <div className="card p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">References</p>
                <dl className="space-y-3 text-xs">
                  {inv.stripe_invoice_id && (
                    <div>
                      <dt className="text-on-surface-variant">Stripe Invoice</dt>
                      <dd className="mt-0.5 break-all font-mono text-on-surface">{inv.stripe_invoice_id}</dd>
                    </div>
                  )}
                  {inv.payment_intent_id && (
                    <div>
                      <dt className="text-on-surface-variant">Payment Intent</dt>
                      <dd className="mt-0.5 break-all font-mono text-on-surface">{inv.payment_intent_id}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </div>
        ),
      };
    }
    if (secondaryType === 'payment' && secondaryData) {
      const inv = secondaryData;
      return {
        open: true,
        onClose: () => { setSecondaryType(null); setSecondaryData(null); },
        title: `$${formatCentsToDollars(inv.amount_paid).toFixed(2)} received`,
        subtitle: inv.invoice_number ? `For ${inv.invoice_number}` : 'Payment details',
        children: (
          <div className="space-y-4">
            <div className="card p-4">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Amount</dt>
                  <dd className="font-semibold text-green-700">${formatCentsToDollars(inv.amount_paid).toFixed(2)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Invoice</dt>
                  <dd className="text-on-surface">{inv.invoice_number || '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-on-surface-variant">Paid</dt>
                  <dd className="text-on-surface">{inv.paid_at ? new Date(inv.paid_at).toLocaleString() : '—'}</dd>
                </div>
              </dl>
            </div>
            {(inv.payment_intent_id || inv.stripe_invoice_id) && (
              <div className="card p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">References</p>
                <dl className="space-y-3 text-xs">
                  {inv.payment_intent_id && (
                    <div>
                      <dt className="text-on-surface-variant">Payment Intent</dt>
                      <dd className="mt-0.5 break-all font-mono text-on-surface">{inv.payment_intent_id}</dd>
                    </div>
                  )}
                  {inv.stripe_invoice_id && (
                    <div>
                      <dt className="text-on-surface-variant">Stripe Invoice</dt>
                      <dd className="mt-0.5 break-all font-mono text-on-surface">{inv.stripe_invoice_id}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </div>
        ),
      };
    }
    return undefined;
  })();

  return (
    <EntityDrawer
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitleId ? `${subtitlePrefix}: ${subtitleId}` : ''}
      headerActions={drawerHeaderActions}
      widthClassName={widthClassName}
      secondary={secondary}
    >
      {customer ? (
        <div className="space-y-6">
          {!!profileCustomer?.historical_stripe_customer_ids?.length && (
            <div role="status" aria-label="Historical billing profiles" className="rounded-lg border border-ghost bg-surface-container-low p-3 text-sm text-on-surface-variant">
              This customer has {profileCustomer.historical_stripe_customer_ids.length} historical billing profile{profileCustomer.historical_stripe_customer_ids.length === 1 ? '' : 's'}. Invoices and saved methods stay linked to their original billing records. Profile edits and new activity use the current customer identity.
            </div>
          )}
          <div className="overflow-x-auto">
            <Tabs
              tabs={[
                { key: 'overview', label: 'Overview' },
                { key: 'invoices', label: 'Invoices', count: customerRecentInvoices.length },
                { key: 'payments', label: 'Payments', count: paidInvoices.length },
              ]}
              activeTab={drawerTab}
              onChange={(tab) => setDrawerTab(tab as CustomerDrawerTab)}
              className="min-w-max"
            />
          </div>

          {drawerTab === 'overview' && (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="card p-4">
                  <h4 className="mb-3 text-sm font-semibold text-on-surface">Profile</h4>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Email</dt>
                      <dd className="break-all text-right text-on-surface">{displayEmail}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Phone</dt>
                      <dd className="text-right text-on-surface">{displayPhone}</dd>
                    </div>
                    {displayExternalCustomerId ? (
                      <div className="flex justify-between gap-2">
                        <dt className="text-on-surface-variant">External Customer ID</dt>
                        <dd className="break-all text-right font-mono text-xs text-on-surface">{displayExternalCustomerId}</dd>
                      </div>
                    ) : null}
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Status</dt>
                      <dd className="text-right text-on-surface">{displayStatus}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Type</dt>
                      <dd className="text-right text-on-surface">{displayType}</dd>
                    </div>
                    {/* <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">KYC</dt>
                      <dd className="text-right text-on-surface">{customer.kycStatus || 'pending'}</dd>
                    </div> */}
                  </dl>
                </div>

                <div className="card p-4">
                  <h4 className="mb-3 text-sm font-semibold text-on-surface">Activity</h4>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Created</dt>
                      <dd className="text-right text-on-surface">{displayCreated ? new Date(displayCreated).toLocaleString() : 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Last Synced</dt>
                      <dd className="text-right text-on-surface">{displayLastActivity ? new Date(displayLastActivity).toLocaleString() : 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Reservations</dt>
                      <dd className="text-right text-on-surface">{customerStats?.totalReservations ?? customer.totalReservations ?? 0}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Total Spent</dt>
                      <dd className="text-right text-on-surface">${Number(customerStats?.totalSpent ?? customer.totalSpent ?? 0).toFixed(2)}</dd>
                    </div>
                    {/* <div className="flex justify-between gap-2">
                      <dt className="text-on-surface-variant">Loyalty Tier</dt>
                      <dd className="text-right text-on-surface">{customerStats?.loyaltyTier || customer.loyaltyTier || 'standard'}</dd>
                    </div> */}
                  </dl>
                </div>
              </div>

              <div className="card p-4">
                <h4 className="mb-3 text-sm font-semibold text-on-surface">CRM Activity</h4>
                {drawerLoading ? (
                  <p className="text-sm text-on-surface-variant">Loading activity...</p>
                ) : crmActivityItems.length === 0 ? (
                  <p className="text-sm text-on-surface-variant">No recent invoice or payment activity for this customer.</p>
                ) : (
                  <div className="space-y-3">
                    {crmActivityItems.map((item) => (
                      <div key={item.id} className="flex items-start justify-between gap-3 rounded-card border border-ghost p-3">
                        <div>
                          <p className="text-sm font-medium text-on-surface">
                            {item.kind === 'grouped_request_sent'
                              ? 'Grouped request sent'
                              : item.kind === 'grouped_request_paid'
                              ? 'Grouped request payment received'
                              : item.kind === 'invoice_sent'
                              ? 'Invoice sent'
                              : 'Payment received'}
                          </p>
                          <p className="mt-1 text-xs text-on-surface-variant">
                            {item.kind === 'grouped_request_sent' || item.kind === 'grouped_request_paid'
                              ? `${item.invoiceLabel} · ${item.invoiceCount || 0} invoices · $${formatCentsToDollars(item.amountCents).toFixed(2)}`
                              : `${item.invoiceLabel} · $${formatCentsToDollars(item.amountCents).toFixed(2)}`}
                          </p>
                        </div>
                        <p className="text-right text-xs text-on-surface-variant">{item.happenedAt.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="card p-4">
                <div>
                  <div>
                    <h4 className="text-sm font-semibold text-on-surface">Account</h4>
                    <p className="mt-1 text-sm text-on-surface-variant">
                      Attach this customer to a first-class account entity.
                    </p>
                  </div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
                  <AccountTypeahead
                    value={selectedAccount}
                    onChange={setSelectedAccount}
                    disabled={!customerEmail || assignAccountMutation.isLoading}
                    allowCreate
                    placeholder="Search account by name..."
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={!selectedAccount || assignAccountMutation.isLoading}
                    onClick={() => {
                      if (selectedAccount) {
                        assignAccountMutation.mutate(selectedAccount);
                      }
                    }}
                  >
                    {assignAccountMutation.isLoading ? 'Saving...' : 'Save Account'}
                  </button>
                </div>
                <div className="mt-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">Linked Accounts</p>
                  {attachedAccounts.length === 0 ? (
                    <p className="mt-2 text-sm text-on-surface-variant">No account is attached to this customer yet.</p>
                  ) : (
                    <div className="mt-3 space-y-2">
                      {attachedAccounts.map((account) => (
                        <div key={account.id} className="flex items-center justify-between gap-3 rounded-card border border-ghost p-3">
                          <button
                            type="button"
                            className="min-w-0 text-left"
                            onClick={() => setSecondaryType('account')}
                          >
                            <p className="truncate text-sm font-medium text-primary hover:underline">{account.name}</p>
                            <p className="mt-1 text-xs text-on-surface-variant">View account details</p>
                          </button>
                          <button
                            type="button"
                            className="btn-secondary"
                            disabled={assignAccountMutation.isLoading}
                            onClick={() => assignAccountMutation.mutate(null)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {!customerEmail ? (
                  <p className="mt-2 text-xs text-on-surface-variant">This customer does not have an email address, so account attachment is unavailable.</p>
                ) : null}
              </div>

              {/* <div className="card p-4">
                <h4 className="mb-2 text-sm font-semibold text-on-surface">Data Source</h4>
                <p className="text-sm text-on-surface-variant">
                  {sourceNote || 'This entity is sourced from Stripe customer objects for the connected tenant account.'}
                </p>
              </div> */}
            </>
          )}

          {drawerTab === 'invoices' && (
            <RecordInvoicesPanel
              contextType="customer"
              invoices={customerRecentInvoices}
              loading={drawerLoading}
              emptyTitle="No invoices for this customer yet"
              emptyDescription="Create the first invoice from this customer record so billing, contact snapshots, and account lineage stay connected."
              createDisabled={!canonicalActivityReady}
              onCreateInvoice={() => { if (canonicalActivityReady) setShowCreateInvoice(true); }}
              onOpenInvoice={(invoice) => setSelectedFullInvoice(invoice)}
              onRefresh={() => {
                queryClient.invalidateQueries(['customer-drawer-invoices', customerEmail]);
                queryClient.invalidateQueries('admin-invoices');
              }}
            />
          )}

          {drawerTab === 'payments' && (
            <div className="space-y-5">
              {canonicalActivityReady
                ? <SavedPaymentMethodsPanel customerId={initialInvoiceCustomer?.id || rawCustomerId} />
                : <p role="status" className="text-sm text-on-surface-variant">Reload the current customer profile before managing payment methods.</p>}
              <h4 className="mb-3 text-sm font-semibold text-on-surface">Payment History</h4>
              {drawerLoading ? (
                <p className="text-sm text-on-surface-variant">Loading payments...</p>
              ) : paidInvoices.length === 0 ? (
                <p className="text-sm text-on-surface-variant">No completed payments found for this customer.</p>
              ) : (
                <div className="space-y-3">
                  {paidInvoices.map((invoice: any) => (
                    <button
                      key={`payment-${invoice.id}`}
                      type="button"
                      onClick={() => { setSecondaryType('payment'); setSecondaryData(invoice); }}
                      className={`w-full rounded-card border p-3 text-left transition-colors hover:bg-surface-container-low ${secondaryType === 'payment' && secondaryData?.id === invoice.id ? 'border-primary bg-surface-container-low' : 'border-ghost'}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-on-surface">Payment for {invoice.invoice_number || 'invoice'}</p>
                          <p className="mt-1 text-xs text-on-surface-variant">
                            Paid {invoice.paid_at ? new Date(invoice.paid_at).toLocaleString() : 'Date unavailable'}
                          </p>
                        </div>
                        <p className="text-sm font-semibold text-green-700">${formatCentsToDollars(invoice.amount_paid).toFixed(2)}</p>
                      </div>
                      <div className="mt-2 space-y-1 text-xs text-on-surface-variant">
                        <p>Payment Intent: {invoice.payment_intent_id || 'N/A'}</p>
                        <p>Stripe Invoice: {invoice.stripe_invoice_id || 'N/A'}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
      {showCreateInvoice ? (
        <CreateInvoiceModal
          onClose={() => setShowCreateInvoice(false)}
          initialCustomer={initialInvoiceCustomer}
          initialAccount={initialInvoiceAccount}
          subtitle="Create an invoice with this customer context prefilled."
          onSuccess={(invoice) => {
            setShowCreateInvoice(false);
            queryClient.invalidateQueries(['customer-drawer-invoices', customerEmail]);
            queryClient.invalidateQueries('admin-invoices');
            if (invoice) {
              setSelectedFullInvoice(invoice);
            }
          }}
        />
      ) : null}
      <InvoiceDrawerHost
        open={Boolean(selectedFullInvoice)}
        invoice={selectedFullInvoice}
        onClose={() => setSelectedFullInvoice(null)}
        onInvoiceUpdated={() => {
          queryClient.invalidateQueries(['customer-drawer-invoices', customerEmail]);
          queryClient.invalidateQueries('admin-invoices');
        }}
      />
    </EntityDrawer>
  );
}
